import { useState } from "react";
import { useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import Button from "../../../../shared/ui/button/Button";
import ConfirmDialog from "../../../../shared/ui/modal/ConfirmDialog";
import {
    CancelSubscriptionDialog,
    isCancelling,
    lineLabel,
    useCancelPlusSubscription,
    checkoutErrorKey,
    useChangePlusTier,
    usePlusSubscription,
    useResumePlusSubscription,
    useSubscription,
} from "../../../subscription";
import { CreditCard, Rocket, Activity } from "lucide-react";
import { useTherapist } from "../../../../context/useTherapist";
import { motion } from "framer-motion";

/** Whole days from today to a date, never negative. */
const daysUntil = (date: Date): number =>
    Math.max(
        0,
        Math.ceil((date.getTime() - Date.now()) / (1000 * 60 * 60 * 24))
    );

export default function SubscriptionStatusCard() {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const { isPremium } = useSubscription();
    const { therapist } = useTherapist();
    const { data: plus } = usePlusSubscription();
    const cancel = useCancelPlusSubscription();
    const resume = useResumePlusSubscription();
    const changeTier = useChangePlusTier();
    const [confirming, setConfirming] = useState(false);
    // The size being moved to, while its confirmation is open.
    const [confirmingTier, setConfirmingTier] = useState<"plus" | "max" | null>(null);

    const language = i18n.language || "es";
    // An account that holds Plus for ever is not on the Plus plan in the
    // sense the word is used everywhere else — nobody is billed for it, and
    // there is nothing to cancel. Saying "Plus" invites them to look for a
    // subscription that does not exist.
    const planLabel = plus?.earlyAdopter
        ? t("subscription.status.earlyAdopterPlan")
        : isPremium
          ? // Plus or Max, from whatever pays for it — a centre seat included.
            t(
                therapist?.plan_tier === "max"
                    ? "subscription.max.title"
                    : "subscription.plus.title"
            )
          : t("subscription.free.title");
    const subscription = plus?.subscription ?? null;
    const periodEnd = subscription?.currentPeriodEnd ?? null;
    // Plus or Max by name, for the sentences about what cancelling keeps.
    const planName = t(`subscription.billing.tier.${subscription?.heldTier ?? "plus"}.name`);
    // Where a move goes from here: up to Max, or back to Plus at the renewal.
    const otherTier = subscription?.tier === "max" ? "plus" : "max";
    const pendingCancellation = plus ? isCancelling(plus) : false;
    // Only an account that actually pays has something to cancel. Plus by
    // invitation or through a centre seat is not this account's to stop, and
    // an early adopter is not being charged at all.
    const isPaying = subscription !== null && subscription.status !== "canceled";
    // What the next invoice is made of, for the period this subscription
    // actually renews on — not a period a screen happens to be showing.
    const quote =
        plus?.quotes.find(
            (candidate) => candidate.period === (subscription?.billingPeriod ?? "monthly")
        ) ?? null;
    const lines = quote?.lines ?? [];
    const money = (cents: number, locale: string) =>
        new Intl.NumberFormat(locale, { style: "currency", currency: "EUR" })
            .format(cents / 100);
    const formatDate = (date: Date) =>
        new Intl.DateTimeFormat(language, { dateStyle: "long" }).format(date);

    const sessionCount = therapist?.monthly_session_count ?? 0;
    const sessionLimit = therapist?.monthly_session_limit;

    return (
        <div className="glass-effect glass-border border rounded-3xl p-8 shadow-theme-sm">
            <div className="flex flex-col gap-8">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-8">
                    <div className="flex items-center gap-4">
                        <div className="w-14 h-14 rounded-2xl bg-brand-500/10 text-brand-500 flex items-center justify-center shadow-theme-xs">
                            <CreditCard className="w-7 h-7" />
                        </div>
                        <div>
                            <h4 className="text-xl font-bold text-content-primary">
                                {t("subscription.status.title")}
                            </h4>
                            <p className="text-sm text-content-tertiary mt-1">
                                {t("subscription.status.description")}
                            </p>
                        </div>
                    </div>

                    <div className="flex flex-col md:flex-row items-center gap-4">
                        <div className={`px-4 py-2 rounded-2xl text-sm font-bold border transition-all ${isPremium
                            ? "bg-accent-tint text-accent-text border-accent/20"
                            : "bg-gray-50 text-gray-700 border-gray-100 dark:bg-white/5 dark:text-gray-400 dark:border-white/10"
                            }`}>
                            {t("subscription.status.currentPlan")}:{" "}
                            <span className="uppercase tracking-wider ml-1">
                                {planLabel}
                            </span>
                        </div>

                        {!isPremium && (
                            <Button
                                onClick={() => navigate("/subscription")}
                                className="shadow-lg shadow-brand-500/20 px-8 py-2.5 font-bold"
                                startIcon={<Rocket className="w-4 h-4" />}
                            >
                                {t("subscription.status.upgrade")}
                            </Button>
                        )}
                    </div>
                </div>

                {/* What paying for Plus means from here: when it renews, and
                    how to stop it. Said on the account screen rather than only
                    on the billing one, because this is where somebody looks
                    for it. */}
                {/* What the plan is made of, for somebody who is paying for
                    it. A plan name and a date say what it costs and when, and
                    nothing at all about what is in it — and the modules are
                    exactly the part somebody forgets they are paying for. */}
                {isPaying && lines.length > 0 && (
                    <div className="space-y-2 border-t border-gray-100 pt-6 dark:border-white/5">
                        <p className="text-sm font-bold uppercase tracking-wider text-content-primary">
                            {t("subscription.status.includes")}
                        </p>
                        {lines.map((line) => (
                            <div key={line.key} className="flex justify-between gap-4 text-sm">
                                <span className="text-content-secondary">
                                    {lineLabel(t, line)}
                                </span>
                                <span className="font-medium text-content-primary">
                                    {money(line.totalCents, language)}
                                </span>
                            </div>
                        ))}
                        {quote && (
                            <div className="flex justify-between gap-4 border-t border-gray-100 pt-2 text-sm font-semibold text-content-primary dark:border-white/5">
                                <span>
                                    {t(
                                        subscription?.billingPeriod === "annual"
                                            ? "subscription.status.totalAnnual"
                                            : "subscription.status.totalMonthly"
                                    )}
                                </span>
                                <span>{money(quote.totalCents, language)}</span>
                            </div>
                        )}
                    </div>
                )}

                {isPaying && periodEnd && (
                    <div className="space-y-3 border-t border-gray-100 pt-6 dark:border-white/5">
                        {pendingCancellation ? (
                            <>
                                <p className="text-sm text-content-secondary">
                                    {t("subscription.status.cancelling", {
                                        date: formatDate(periodEnd),
                                        plan: planName,
                                    })}
                                </p>
                                <Button
                                    variant="outline"
                                    loading={resume.isPending}
                                    onClick={() => resume.mutate()}
                                >
                                    {t("subscription.billing.resume")}
                                </Button>
                            </>
                        ) : (
                            <>
                                <p className="text-sm text-content-secondary">
                                    {subscription?.inTrial && subscription.trialEndsAt
                                        ? t("subscription.billing.trial.until", {
                                              date: formatDate(subscription.trialEndsAt),
                                          })
                                        : daysUntil(periodEnd) === 0
                                        ? t("subscription.status.renewsToday")
                                        : t("subscription.status.renewsIn", {
                                              count: daysUntil(periodEnd),
                                              date: formatDate(periodEnd),
                                          })}
                                </p>
                                {/* The consequence, before the button and not
                                    only inside the dialog: what stops, what
                                    they keep, and until when. */}
                                {subscription &&
                                    subscription.tier !== subscription.heldTier &&
                                    subscription.maxUntil && (
                                        <p className="text-caption text-content-tertiary">
                                            {t("subscription.billing.tier.maxUntil", {
                                                date: formatDate(subscription.maxUntil),
                                            })}
                                        </p>
                                    )}
                                <p className="text-caption text-content-tertiary">
                                    {t("subscription.status.cancelWarning", {
                                        date: formatDate(periodEnd),
                                        plan: planName,
                                    })}
                                </p>
                                <div className="flex flex-wrap gap-2">
                                    {/* Moving size from here, without a trip to
                                        the billing screen. */}
                                    <Button
                                        disabled={changeTier.isPending}
                                        onClick={() => setConfirmingTier(otherTier)}
                                    >
                                        {t(
                                            otherTier === "max"
                                                ? "subscription.billing.tier.toMax"
                                                : "subscription.billing.tier.toPlus"
                                        )}
                                    </Button>
                                    <Button
                                        variant="outline"
                                        disabled={cancel.isPending}
                                        onClick={() => setConfirming(true)}
                                    >
                                        {t("subscription.billing.cancel")}
                                    </Button>
                                    <Button
                                        variant="outline"
                                        onClick={() => navigate("/subscription/billing")}
                                    >
                                        {t("subscription.status.manage")}
                                    </Button>
                                </div>
                            </>
                        )}

                        <CancelSubscriptionDialog
                            isOpen={confirming}
                            title={t("subscription.billing.cancelConfirm.title", {
                                plan: planName,
                            })}
                            message={t("subscription.status.cancelWarning", {
                                date: formatDate(periodEnd),
                                plan: planName,
                            })}
                            isBusy={cancel.isPending}
                            onConfirm={(why) => {
                                cancel.mutate(why);
                                setConfirming(false);
                            }}
                            onClose={() => setConfirming(false)}
                        />

                        <ConfirmDialog
                            isOpen={confirmingTier !== null}
                            title={t(
                                confirmingTier === "max"
                                    ? "subscription.billing.tier.confirmMax.title"
                                    : "subscription.billing.tier.confirmPlus.title"
                            )}
                            message={t(
                                confirmingTier === "max"
                                    ? subscription?.heldTier === "max"
                                        ? "subscription.billing.tier.confirmMax.messageAlreadyPaid"
                                        : "subscription.billing.tier.confirmMax.message"
                                    : "subscription.billing.tier.confirmPlus.message",
                                { date: formatDate(periodEnd) }
                            )}
                            confirmLabel={t(
                                confirmingTier === "max"
                                    ? "subscription.billing.tier.confirmMax.confirm"
                                    : "subscription.billing.tier.confirmPlus.confirm"
                            )}
                            isBusy={changeTier.isPending}
                            onConfirm={() => {
                                if (confirmingTier) changeTier.mutate(confirmingTier);
                                setConfirmingTier(null);
                            }}
                            onCancel={() => setConfirmingTier(null)}
                        />
                        {changeTier.isError && (
                            <p role="alert" className="text-sm text-red-600 dark:text-red-400">
                                {t(checkoutErrorKey(changeTier.error))}
                            </p>
                        )}
                    </div>
                )}

                {sessionLimit !== undefined && sessionLimit !== null && (
                    <div className="pt-6 border-t border-gray-100 dark:border-white/5">
                        <div className="flex items-center gap-2 mb-4">
                            <Activity className="w-4 h-4 text-brand-500" />
                            <span className="text-sm font-bold text-content-primary uppercase tracking-wider">
                                {t("subscription.usage.sessionsThisMonth")}
                            </span>
                        </div>
                        
                        <div className="flex justify-between items-end mb-2">
                            <div className="flex items-baseline gap-1">
                                <span className="text-2xl font-bold text-content-primary">{sessionCount}</span>
                                <span className="text-content-tertiary text-sm">/ {sessionLimit}</span>
                            </div>
                            <span className="text-xs font-medium text-content-tertiary">
                                {Math.round((sessionCount / sessionLimit) * 100)}%
                            </span>
                        </div>

                        <div className="w-full h-2 bg-gray-100 dark:bg-white/5 rounded-full overflow-hidden">
                            <motion.div 
                                initial={{ width: 0 }}
                                animate={{ width: `${Math.min((sessionCount / sessionLimit) * 100, 100)}%` }}
                                transition={{ duration: 1, ease: "easeOut" }}
                                className={`h-full rounded-full ${
                                    (sessionCount / sessionLimit) > 0.9 ? 'bg-red-500' : 'bg-brand-500'
                                }`}
                            />
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
